// File System Adapter
// Abstraction layer for OPFS (memory) and File System Access API (disk)
import { createWorker } from 'opfs-worker';
import { get, set, del } from 'idb-keyval';
import { Logger } from './logger';
const console = new Logger('FileSystemAdapter');
// OPFS Adapter for memory-based file system
export class OPFSAdapter {
    constructor() {
        this.fs = null;
    }
    async getFS() {
        if (!this.fs) {
            this.fs = await createWorker();
        }
        return this.fs;
    }
    async scanDirectory(path = '/') {
        try {
            const fs = await this.getFS();
            const dirHandle = await fs.readDir(path);
            const items = {};
            for (const dirEnt of dirHandle || []) {
                const is_dir = dirEnt.kind === 'directory';
                let size = 0;
                let mtime = '';
                const name = dirEnt.name;
                const itemPath = path === '/' ? `/${name}` : `${path}/${name}`;
                if (!is_dir) {
                    const fullPath = path === '/'
                        ? `/${dirEnt.name}`
                        : `${path}/${dirEnt.name}`;
                    try {
                        const stat = await fs.stat(fullPath);
                        size = stat.size;
                        mtime = stat.mtime;
                    }
                    catch (e) {
                        continue;
                    }
                }
                items[name] = { path: itemPath, is_dir, size, mtime };
            }
            return items;
        }
        catch (error) {
            console.error('[OPFSAdapter]', 'Error scanning directory:', error);
            return {};
        }
    }
    async readFile(path) {
        const fs = await this.getFS();
        // Force binary mode since files are written as Uint8Array
        // This prevents UTF-8 decoding corruption of binary plist files
        return await fs.readFile(path, 'binary');
    }
    async writeFile(path, content) {
        const fs = await this.getFS();
        // Force binary mode for consistent Uint8Array handling
        await fs.writeFile(path, content, 'binary');
    }
    async createFolder(path) {
        const fs = await this.getFS();
        await fs.mkdir(path, { recursive: true });
    }
    async deleteItem(path, isDir) {
        const fs = await this.getFS();
        await fs.remove(path, { recursive: isDir });
    }
    async renameItem(oldPath, newName, isDir) {
        const fs = await this.getFS();
        const parentPath = oldPath.substring(0, oldPath.lastIndexOf('/')) || '/';
        const newPath = parentPath === '/' ? `/${newName}` : `${parentPath}/${newName}`;
        await fs.rename(oldPath, newPath);
    }
    async fileExists(path) {
        try {
            const fs = await this.getFS();
            await fs.stat(path);
            return true;
        }
        catch (error) {
            return false;
        }
    }
}
// Native File System Access API Adapter for disk-based file system.
// Each instance persists its directory handle under a dedicated IndexedDB key
// so Fonts folder and Settings folder remain independent.
export const FONTS_FOLDER_HANDLE_KEY = 'disk-directory-handle';
export const SETTINGS_FOLDER_HANDLE_KEY = 'settings-directory-handle';
export class NativeAdapter {
    constructor(storageKey = FONTS_FOLDER_HANDLE_KEY) {
        this.directoryHandle = null;
        this.rootPath = '';
        this.storageKey = storageKey;
    }
    /** The persisted IndexedDB key for this adapter's directory handle. */
    getHandleStorageKey() {
        return this.storageKey;
    }
    /** The currently selected directory handle, if any. */
    getDirectoryHandle() {
        return this.directoryHandle;
    }
    async initialize() {
        // Try to restore persisted directory handle
        const handle = await get(this.storageKey);
        if (handle && handle instanceof FileSystemDirectoryHandle) {
            this.directoryHandle = handle;
            this.rootPath = '/';
            console.log('[NativeAdapter]', `Restored directory handle (${this.storageKey}):`, handle.name);
            return true;
        }
        return false;
    }
    async selectDirectory(options) {
        const pickerOptions = {
            mode: 'readwrite',
            id: this.storageKey
        };
        if (options?.startIn) {
            pickerOptions.startIn = options.startIn;
        }
        if (!isFileSystemAccessSupported()) {
            console.warn('[NativeAdapter]', 'Directory picker is not available in this browser');
            return false;
        }
        let pickerPromise;
        try {
            // Start the native picker in this turn so it stays in the user
            // gesture. Awaiting anything first (module load, IndexedDB) delays
            // or blocks Chrome's first showDirectoryPicker.
            pickerPromise = window.showDirectoryPicker(pickerOptions);
        }
        catch (error) {
            const err = error;
            if (err.name === 'AbortError') {
                console.log('[NativeAdapter]', 'Directory selection cancelled');
                return false;
            }
            console.error('[NativeAdapter]', 'Error selecting directory:', error);
            throw error;
        }
        try {
            const handle = await pickerPromise;
            this.directoryHandle = handle;
            this.rootPath = '/';
            await set(this.storageKey, handle);
            await this.requestPermission();
            console.log('[NativeAdapter]', `Selected directory (${this.storageKey}):`, handle.name);
            return true;
        }
        catch (error) {
            const err = error;
            if (err.name === 'AbortError') {
                console.log('[NativeAdapter]', 'Directory selection cancelled');
                return false;
            }
            console.error('[NativeAdapter]', 'Error selecting directory:', error);
            throw error;
        }
    }
    async clearDirectory() {
        await del(this.storageKey);
        this.directoryHandle = null;
        this.rootPath = '';
        console.log('[NativeAdapter]', `Directory handle cleared (${this.storageKey})`);
    }
    hasDirectory() {
        return this.directoryHandle !== null;
    }
    getDirectoryName() {
        return this.directoryHandle?.name || '';
    }
    async checkPermission() {
        if (!this.directoryHandle) {
            return 'prompt';
        }
        try {
            const permission = await this.directoryHandle.queryPermission({ mode: 'readwrite' });
            return permission;
        }
        catch (error) {
            console.error('[NativeAdapter]', 'Error checking permission:', error);
            return 'denied';
        }
    }
    async requestPermission() {
        if (!this.directoryHandle) {
            return 'prompt';
        }
        try {
            const permission = await this.directoryHandle.requestPermission({ mode: 'readwrite' });
            console.log('[NativeAdapter]', 'Permission status:', permission);
            return permission;
        }
        catch (error) {
            console.error('[NativeAdapter]', 'Error requesting permission:', error);
            return 'denied';
        }
    }
    async getHandleAtPath(path) {
        if (!this.directoryHandle) {
            throw new Error('No directory selected');
        }
        // Normalize path
        const normalizedPath = path.replace(/^\/+/, '');
        if (!normalizedPath) {
            return this.directoryHandle;
        }
        const parts = normalizedPath.split('/');
        let currentHandle = this.directoryHandle;
        for (let i = 0; i < parts.length - 1; i++) {
            currentHandle = await currentHandle.getDirectoryHandle(parts[i]);
        }
        const lastName = parts[parts.length - 1];
        try {
            return await currentHandle.getFileHandle(lastName);
        }
        catch {
            return await currentHandle.getDirectoryHandle(lastName);
        }
    }
    async scanDirectory(path = '/') {
        if (!this.directoryHandle) {
            return {};
        }
        try {
            const handle = await this.getHandleAtPath(path);
            if (!(handle instanceof FileSystemDirectoryHandle)) {
                return {};
            }
            const items = {};
            for await (const [name, childHandle] of handle.entries()) {
                const is_dir = childHandle.kind === 'directory';
                const itemPath = path === '/' ? `/${name}` : `${path}/${name}`;
                let size = 0;
                let mtime = '';
                if (!is_dir && childHandle instanceof FileSystemFileHandle) {
                    try {
                        const file = await childHandle.getFile();
                        size = file.size;
                        mtime = new Date(file.lastModified).toISOString();
                    }
                    catch (e) {
                        continue;
                    }
                }
                items[name] = {
                    path: itemPath,
                    is_dir,
                    size,
                    mtime,
                    handle: childHandle
                };
            }
            return items;
        }
        catch (error) {
            console.error('[NativeAdapter]', 'Error scanning directory:', error);
            return {};
        }
    }
    async readFile(path) {
        const handle = await this.getHandleAtPath(path);
        if (!(handle instanceof FileSystemFileHandle)) {
            throw new Error('Path is not a file');
        }
        const file = await handle.getFile();
        const arrayBuffer = await file.arrayBuffer();
        return new Uint8Array(arrayBuffer);
    }
    async writeFile(path, content) {
        if (!this.directoryHandle) {
            throw new Error('No directory selected');
        }
        const normalizedPath = path.replace(/^\/+/, '');
        if (!normalizedPath) {
            throw new Error('Invalid file path');
        }
        const parts = normalizedPath.split('/').filter(Boolean);
        const fileName = parts.pop();
        if (!fileName) {
            throw new Error('Invalid file path');
        }
        let parentHandle = this.directoryHandle;
        for (const part of parts) {
            parentHandle = await parentHandle.getDirectoryHandle(part, {
                create: true
            });
        }
        const fileHandle = await parentHandle.getFileHandle(fileName, {
            create: true
        });
        const writable = await fileHandle.createWritable();
        if (typeof content === 'string') {
            await writable.write(content);
        }
        else {
            await writable.write(new Uint8Array(content));
        }
        await writable.close();
    }
    async createFolder(path) {
        if (!this.directoryHandle) {
            throw new Error('No directory selected');
        }
        const normalizedPath = path.replace(/^\/+/, '');
        const parts = normalizedPath.split('/');
        let currentHandle = this.directoryHandle;
        for (const part of parts) {
            currentHandle = await currentHandle.getDirectoryHandle(part, {
                create: true
            });
        }
    }
    async deleteItem(path, isDir) {
        if (!this.directoryHandle) {
            throw new Error('No directory selected');
        }
        const normalizedPath = path.replace(/^\/+/, '');
        const parts = normalizedPath.split('/');
        const fileName = parts.pop();
        let currentHandle = this.directoryHandle;
        for (const part of parts) {
            currentHandle = await currentHandle.getDirectoryHandle(part);
        }
        await currentHandle.removeEntry(fileName, { recursive: isDir });
    }
    async renameItem(oldPath, newName, isDir) {
        if (!this.directoryHandle) {
            throw new Error('No directory selected');
        }
        const normalizedPath = oldPath.replace(/^\/+/, '');
        const parts = normalizedPath.split('/');
        const oldName = parts.pop();
        // Get parent directory handle
        let parentHandle = this.directoryHandle;
        for (const part of parts) {
            parentHandle = await parentHandle.getDirectoryHandle(part);
        }
        // File System Access API doesn't have a rename method
        // We need to use move() which is available in modern browsers
        const sourceHandle = isDir
            ? await parentHandle.getDirectoryHandle(oldName)
            : await parentHandle.getFileHandle(oldName);
        // Use the move() method (Chrome 110+)
        if ('move' in sourceHandle) {
            await sourceHandle.move(newName);
        }
        else {
            throw new Error('Rename not supported in this browser. Please use Chrome 110+ or Edge 110+.');
        }
    }
    async getFileHandle(path) {
        try {
            const handle = await this.getHandleAtPath(path);
            if (handle instanceof FileSystemFileHandle) {
                return handle;
            }
        }
        catch (error) {
            console.error('[NativeAdapter]', 'Error getting file handle:', error);
        }
        return null;
    }
    async fileExists(path) {
        try {
            await this.getHandleAtPath(path);
            return true;
        }
        catch (error) {
            return false;
        }
    }
    /**
     * Recursively list all files in a directory up to maxDepth levels
     * @param path Starting path
     * @param maxDepth Maximum depth to traverse (default 3)
     * @returns Array of FileInfo for all files found
     */
    async listFilesRecursive(path = '/', maxDepth = 3) {
        if (!this.directoryHandle) {
            return [];
        }
        const results = [];
        const scanDir = async (dirPath, depth) => {
            if (depth > maxDepth)
                return;
            try {
                const handle = await this.getHandleAtPath(dirPath);
                if (!(handle instanceof FileSystemDirectoryHandle)) {
                    return;
                }
                for await (const [name, childHandle] of handle.entries()) {
                    const isDir = childHandle.kind === 'directory';
                    const itemPath = dirPath === '/' ? `/${name}` : `${dirPath}/${name}`;
                    if (isDir) {
                        await scanDir(itemPath, depth + 1);
                    }
                    else {
                        let size = 0;
                        let mtime = '';
                        try {
                            const file = await childHandle.getFile();
                            size = file.size;
                            mtime = new Date(file.lastModified).toISOString();
                        }
                        catch (e) {
                            continue;
                        }
                        results.push({
                            path: itemPath,
                            is_dir: false,
                            size,
                            mtime,
                            handle: childHandle
                        });
                    }
                }
            }
            catch (error) {
                // Directory doesn't exist or can't be read
                console.log('[NativeAdapter]', `Cannot scan ${dirPath}:`, error);
            }
        };
        await scanDir(path, 1);
        return results;
    }
}
// Check if File System Access API is supported
export function isFileSystemAccessSupported() {
    return (typeof window
        .showDirectoryPicker === 'function');
}
